import type { StrategyNode } from '../../../../types/BaseNode'
import { SingleComponentCard } from './SingleComponentCard'
import { CompositeComponentCard } from './CompositeComponentCard'
import type { SingleNode } from '../../../../types/SingleNode'
import type { CompositeNode } from '../../../../types/CompositeNode'
import { FormatterComponentCard } from './FormatterComponentCard'
import type { FormatterNode } from '../../../../types/FormatterNode'
import { PnLComponentCard } from './PnLComponentCard'
import type { PnLNode } from '../../../../types/PnLNode'
import { VarianceComponentCard } from './VarianceComponentCard'
import type { VarianceNode } from '../../../../types/VarianceNode'
import { MeepMeepCard } from './MeepMeepCard'
import type { MeepMeepNode } from '../../../../types/MeepMeepNode'
import { RegressionCard } from './RegressionCard'
import type { RegressionNode } from '../../../../types/RegressionNode'
import { useState, useRef, useLayoutEffect } from 'react';
import { SaveToMonitorModal } from '../../monitor/SaveToMonitorModal';
import { saveStrategy } from '../../../../utils/storage'
// import type { ErrorNode } from '../../types/ErrorNode'
import { serializeNode } from '../../../../utils/serializeNode'
import { cloneNode } from '../../../../utils/handleDrop'
import { replaceNodeInTree } from '../../../../utils/treeHandler'
import { reinstantiate } from '../../../../utils/nodeFactory'
import { useDataTypes } from '../../../../hooks/useDataTypes'
import { DynamicField } from '../../common/DynamicField'
import { useMonitorContext } from '../../../../contexts/MonitorContext'
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext'
import { useOperators } from '../../../../hooks/useOperators'
import { useAutoNaming } from '../../../../hooks/useAutoNaming'
import { Tooltip } from '../../common/Tooltip'
import { MeepMeepBadge } from '../../common/MeepMeepBadge'
import type { StrategyFamily } from '../../../../types/Strategy'
import './StrategyNodeCard.css' // Import styles
import '../../../../styles/MeepMeep.css' // MeepMeep styles

interface Props {
  node: StrategyNode;
  slot?: 'left' | 'right' | 'child' | 'root';
  parentId?: string | null;
  parentOperator?: string;
  parentType?: StrategyNode['type'];
  strategyFamily?: StrategyFamily;
}

const DISPLAY_TYPE_LABELS: Record<string, string> = {
  Single: "Data",
  Composite: "Combine",
  Formatter: "Transform",
  PnL: "PnL",
  Variance: "Variance",
  MeepMeep: "MeepMeep Strategy",
  Regression: "Regression Model"
};

const BORDER_EXPLANATIONS: Record<string, string> = {
  Single: "Data Node: Provides initial data for the strategy.",
  Composite: "Combine Node: Merges data from multiple sources.",
  Formatter: "Transform Node: Modifies or formats data.",
  PnL: "PnL Node: Analyzes profit and loss metrics.",
  Variance: "Variance Node: Calculates deviation and variance.",
  MeepMeep: "MeepMeep Root: High-frequency microstructure backtest with dual-slot inputs.",
  Regression: "Regression Node: Multi-feature model for alpha signal generation."
};

export function StrategyNodeCard({ node, slot, parentId, parentOperator, parentType, strategyFamily = 'base' }: Props) {
  const { onUpdate, onDelete, onWrapInComposite, onWrapInFormatter, onCalculate, onMakeRoot, onPromoteToParent, onStrategySaved, toggleCollapsed, isCollapsed, errorNode, onCreateStrategyFromNode, rootNode, reportDimensions, setElevatedId } = useNodeOperations();
  const { monitorId, conditionRootId, setConditionRootId } = useMonitorContext();
  const { getOperatorFlags } = useOperators();
  const cardRef = useRef<HTMLDivElement>(null);

  // Dynamic measurement: report actual size back to layout engine
  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { inlineSize, blockSize } = entry.borderBoxSize[0];
        reportDimensions(node.id, inlineSize, blockSize);
      }
    });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [node.id, reportDimensions]);

  const [menuOpen, setMenuOpen] = useState(false);

  // ── Defensive guard: if node.type is undefined (malformed/raw JSON node) render a safe fallback
  if (!node || !node.type) {
    return (
      <div style={{ border: '2px dashed red', borderRadius: 6, padding: '8px 12px', color: 'red', fontSize: 12, minWidth: 140 }}>
        ⚠️ Malformed node
        <div style={{ opacity: 0.6, fontSize: 10 }}>{node?.id || 'unknown id'}</div>
      </div>
    );
  }

  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [strategyName, setStrategyName] = useState('');
  const [showMonitorModal, setShowMonitorModal] = useState(false);
  const [monitorPosition, setMonitorPosition] = useState<{ x: number, y: number } | null>(null);

  const toggleMenu = () => {
    const next = !menuOpen;
    setMenuOpen(next);
    onUpdateElevatedId(next ? node.id : null);
  };
  const closeMenu = () => {
    setMenuOpen(false);
    onUpdateElevatedId(null);
  };

  // Helper to ensure we don't crash or cause issues
  const onUpdateElevatedId = (id: string | null) => {
    setElevatedId(id);
  };

  //looping
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(node.title || '');
  const [showLoopPrompt, setShowLoopPrompt] = useState(false);

  const { getDataTypeById, getOptions } = useDataTypes();
  const { getSuggestedName, loading: namingLoading } = useAutoNaming();

  const typeId = (node as any).config?.typeId || "";
  const selectedType = typeId ? getDataTypeById(typeId) : null;

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (editedTitle.trim() !== node.title) {
      onUpdate(node.id, { ...node, title: editedTitle.trim(), isManualRename: true });
    }
  };

  // ── Auto Naming Logic ───────────────────────────────────────────
  useLayoutEffect(() => {
    // If the node was manually renamed, we do NOT auto-update
    if (node.isManualRename) {
      return;
    }
    // Wait until data types are loaded before attempting to name
    if (namingLoading) {
      return;
    }

    const suggested = getSuggestedName(node);
    if (!suggested || suggested === node.title) {
      return;
    }

    // Auto-update if not manual and suggestion changed
    onUpdate(node.id, { ...node, title: suggested });
  }, [node, namingLoading, getSuggestedName, onUpdate]);

  const [selectedField, setSelectedField] = useState<any>(null);
  const [tempValue, setTempValue] = useState<any>(null);
  const [tempLabel, setTempLabel] = useState<string>("");
  const [loopValues, setLoopValues] = useState<{ value: any, label: string }[]>([]);

  // Translation feedback state
  const [translationFeedback, setTranslationFeedback] = useState<{
    success: boolean;
    message: string;
    details?: string[];
  } | null>(null);



  const renderTitle = () => {
    if (isEditingTitle) {
      return (
        <input
          autoFocus
          className="node-title-input"
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          onBlur={handleTitleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleTitleBlur();
            if (e.key === 'Escape') {
              setIsEditingTitle(false);
              setEditedTitle(node.title || '');
            }
          }}
        />
      );
    }
    return (
      <span
        onDoubleClick={() => setIsEditingTitle(true)}
        className="node-title-text"
        title="Double-click to rename, Drag to copy"
        style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {node.title || DISPLAY_TYPE_LABELS[node.type] || `Node: ${node.type}`}
      </span>
    );
  };

  const renderContent = () => {
    const hydratedNode = reinstantiate(node);
    if (node.type === 'Single') {
      return (
        <SingleComponentCard 
            node={hydratedNode as SingleNode} 
            parentOperator={parentOperator}
            slot={slot}
            parentType={parentType}
            strategyFamily={strategyFamily}
        />
      );
    }
    if (node.type === 'Composite') {
      return (
        <CompositeComponentCard node={node as CompositeNode} />
      );
    }
    if (node.type === 'Formatter') {
      return (
        <FormatterComponentCard node={node as FormatterNode} strategyFamily={strategyFamily} />
      );
    }
    if (node.type === 'PnL') {
      return (
        <PnLComponentCard node={node as PnLNode} />
      );
    }
    if (node.type === 'Variance') {
      return (
        <VarianceComponentCard node={node as VarianceNode} />
      );
    }
    if (node.type === 'MeepMeep') {
      return (
        <MeepMeepCard node={hydratedNode as MeepMeepNode} />
      );
    }
    if (node.type === 'Regression') {
      return (
        <RegressionCard node={hydratedNode as RegressionNode} />
      );
    }
    return <div>⚠️ Unknown node type: {JSON.stringify(node)}</div>;
  };

  const hasError = errorNode?.nodeId === node.id;
  const isConditionRoot = node.id === conditionRootId;

  // ── Contextual Highlighting ─────────────────────────────────────

  const contextualBorder = () => {
    if (!parentId) return undefined;

    const { isFilter, isComparison } = getOperatorFlags(parentOperator);

    if (slot === 'left') {
      if (isFilter) return '3px solid var(--connector-filter)';
      if (isComparison) return '3px solid var(--accent-success)';
    }
    return undefined;
  };

  const getBorderTooltipContent = () => {
    if (hasError) return "Error: This node has validation issues.";
    if (isConditionRoot) return "root of condition tree";

    if (parentId) {
      const { isFilter, isComparison } = getOperatorFlags(parentOperator);
      if (slot === 'left') {
        if (isFilter) return "Data subject to filter";
        if (isComparison) return "data subject to comparison";
      }
      if (slot === 'right') {
        if (isFilter || isComparison) return "Data only used as filter";
      }
    }
    return BORDER_EXPLANATIONS[node.type] || `Node Type: ${node.type}`;
  };

  return (
    <Tooltip content={getBorderTooltipContent()} position="top">
      <div
        ref={cardRef}
        className={`node-card node-type-${node.type.toLowerCase()} ${hasError ? 'error' : ''} ${isConditionRoot ? 'condition-root' : ''}`}
        style={{ border: contextualBorder() }}
      >
        <div className="node-header">

          {/* Dropdown menu trigger */}
          <div style={{ position: 'relative' }}>
            <button className="action-btn" onClick={toggleMenu} title="More actions">⋯</button>

            {menuOpen && (
              <div className="menu-dropdown" onMouseLeave={closeMenu}>
                <button onClick={() => { onDelete(node.id); closeMenu(); }} className="menu-item">❌ Delete Node</button>
                <button onClick={() => { onMakeRoot(node.id); closeMenu(); }} className="menu-item">⬆️ Make Root</button>
                <button onClick={() => { onPromoteToParent(node.id); closeMenu(); }} className="menu-item">🔼 Promote</button>
                <button onClick={() => { setShowSavePrompt(true); closeMenu(); }} className="menu-item">💾 Save Strategy</button>
                <button onClick={() => { onCreateStrategyFromNode(node); closeMenu(); }} className="menu-item">➡️ Strategy from this</button>
                <button onClick={() => { setShowLoopPrompt(true); closeMenu(); }} className="menu-item">🔁 Loop Values</button>
                <button onClick={(e) => {
                  setMonitorPosition({ x: e.clientX, y: e.clientY });
                  setShowMonitorModal(true);
                  closeMenu();
                }} className="menu-item">
                  {monitorId ? '🛡️ Update Monitoring' : '🛡️ Monitor this Node'}
                </button>
              
              </div>
            )}
          </div>

          <div
            draggable
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => {
              e.dataTransfer.setData("application/moneymaker-subtree", JSON.stringify(serializeNode(node, null, null)));
              e.dataTransfer.effectAllowed = "copy";
            }}
            className="node-title-container"
          >
            {renderTitle()}
          </div>

          <div className="meep-badges-container">
            {node.isPayloadAware && <MeepMeepBadge type="payload" />}
            {node.isMicrostructure && <MeepMeepBadge type="microstructure" />}
          </div>

          {/* Right Actions */}
          <div className="node-actions">
            {node.type !== 'PnL' && node.type !== 'Variance' && <button className="action-btn" title="Wrap in combiner" onClick={() => onWrapInComposite(node.id)}>⬉⬈</button>}
            {node.type !== 'PnL' && node.type !== 'Variance' && <button className="action-btn" title="Wrap in transformer" onClick={() => onWrapInFormatter(node.id)}>🪄</button>}
            <button className="action-btn" title="Calculate Subtree" onClick={() => onCalculate(node)}>📅</button>
            {node.type !== 'Single' && (
              <button
                className="action-btn"
                title={isCollapsed(node.id) ? "Expand" : "Collapse"}
                onClick={() => toggleCollapsed(node.id)}
              >
                {isCollapsed(node.id) ? "➕" : "➖"}
              </button>
            )}
          </div>
        </div>

        {hasError && (
          <div className="error-banner" title={errorNode?.hint}>
            ⚠️ {errorNode?.message}
          </div>
        )}

        {/* Translation Feedback Banner */}
        {translationFeedback && (
          <div
            style={{
              margin: '8px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: translationFeedback.success ? 'rgba(46, 204, 113, 0.15)' : 'rgba(231, 76, 60, 0.15)',
              border: `1px solid ${translationFeedback.success ? 'rgba(46, 204, 113, 0.4)' : 'rgba(231, 76, 60, 0.4)'}`,
              color: translationFeedback.success ? '#2ecc71' : '#e74c3c',
              fontSize: '12px',
              position: 'relative'
            }}
          >
            <button
              onClick={() => setTranslationFeedback(null)}
              style={{
                position: 'absolute', right: '4px', top: '4px', background: 'transparent',
                border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '14px', lineHeight: '1'
              }}
              title="Dismiss"
            >
              ✖
            </button>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', paddingRight: '16px' }}>
              {translationFeedback.success ? '✅ Success' : '❌ Error'}
            </div>
            <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
              {translationFeedback.message}
              {translationFeedback.details && translationFeedback.details.length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: '16px', opacity: 0.9 }}>
                  {translationFeedback.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="node-content">
          {isCollapsed(node.id) ? (
            <div style={{ fontStyle: "italic", opacity: 0.5 }}>[collapsed]</div>
          ) : (
            renderContent()
          )}
        </div>

        {/* --- Modals (Loops/Save/Monitor) --- */}
        {showSavePrompt && (
          <div className="modal-overlay" onClick={() => setShowSavePrompt(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>Save Strategy</h3>
              <input
                type="text"
                value={strategyName}
                onChange={(e) => setStrategyName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  margin: '1rem 0',
                  borderRadius: '4px',
                  border: '1px solid var(--surface-glass-border)',
                  background: 'var(--bg-app)',
                  color: 'var(--text-primary)'
                }}
                placeholder="e.g. My Spread Strategy"
                autoFocus
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button onClick={() => setShowSavePrompt(false)}>Cancel</button>
                <button onClick={() => {
                  saveStrategy(strategyName, node);
                  setStrategyName('');
                  setShowSavePrompt(false);
                  onStrategySaved?.();
                }}>Save</button>
              </div>
            </div>
          </div>
        )}

        {showLoopPrompt && (
          <div className="modal-overlay" onClick={() => setShowLoopPrompt(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h3>Loop Strategy</h3>
              {/* Simplified Loop UI for brevity - using inline for complex logic internal parts if needed, or refactor later */}
              <p>Select a parameter to loop over...</p>
              {selectedType ? (
                <>
                  <select
                    style={{
                      width: "100%",
                      margin: "0.5rem 0",
                      padding: '0.5rem',
                      background: 'var(--bg-app)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--surface-glass-border)',
                      borderRadius: '4px'
                    }}
                    value={selectedField?.key || ""}
                    onChange={(e) => {
                      const fs = selectedType.properties.find((p: any) => p.key === e.target.value);
                      setSelectedField(fs || null);
                    }}
                  >
                    <option value="">-- Select field --</option>
                    {selectedType?.properties.map((p: any) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>

                  {selectedField && (
                    <div className="mb-md">
                      <DynamicField spec={selectedField} context={{}} value={tempValue} onChange={(v, lbl) => { setTempValue(v); setTempLabel(lbl ?? String(v)); }} getOptions={getOptions} />
                      <button onClick={() => {
                        if (tempValue !== null) {
                          setLoopValues(p => [...p, { value: tempValue, label: tempLabel || String(tempValue) }]);
                          setTempValue(null);
                        }
                      }} style={{ marginTop: '0.5rem' }}>Add Value</button>

                      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
                        {loopValues.map((v, i) => (
                          <span key={i} style={{
                            background: 'var(--surface-glass-highlight)',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--surface-glass-border)'
                          }}>
                            {v.label} <span onClick={() => setLoopValues(p => p.filter((_, idx) => idx !== i))} style={{ cursor: 'pointer', marginLeft: '0.5rem', color: 'var(--accent-danger)' }}>x</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
                    <button onClick={() => setShowLoopPrompt(false)}>Cancel</button>
                    <button disabled={loopValues.length === 0} onClick={() => {
                      loopValues.forEach(val => {
                        const editedNode = cloneNode(node);
                        if (editedNode.type === "Single" || editedNode.type === "Formatter") {
                          if (!editedNode.config) editedNode.config = {};
                          if (!editedNode.config.props) editedNode.config.props = {};
                          editedNode.config.props[selectedField.key] = val.value;
                        }
                        const replaced = replaceNodeInTree(rootNode!, node.id, editedNode)!;
                        const rootCopy = cloneNode(replaced);
                        rootCopy.title = `${rootCopy.title || "Strategy"} (${val.label})`;
                        onCreateStrategyFromNode(rootCopy);
                      });
                      setShowLoopPrompt(false);
                    }}>Create Loops</button>
                  </div>
                </>
              ) : (
                <p>Only available for typed nodes.</p>
              )}
            </div>
          </div>
        )}

        {showMonitorModal && (
          <SaveToMonitorModal
            node={node}
            rootNode={rootNode!}
            start={null}
            end={null}
            position={monitorPosition}
            onClose={() => setShowMonitorModal(false)}
            onSuccess={() => {
              if (monitorId && setConditionRootId) {
                setConditionRootId(node.id);
              }
              alert('Monitoring successfully started/updated!');
            }}
            monitorId={monitorId}
          />
        )}
      </div>
    </Tooltip>
  );
}


